// src/services/vendorService.js
import { repo } from "../dbservice/repo";
import { VendorModel } from "../models/VendorModel";

class VendorService {
  constructor() {
    this.collectionName = "vendors";
  }

  get currentUser() {
    if (typeof window === "undefined") return null;
    try {
      const data = localStorage.getItem("userData");
      return data ? JSON.parse(data) : null;
    } catch (e) {
      console.error("[VendorService] Failed to parse userData", e);
      return null;
    }
  }

  /**
   * Create a new vendor with automatic audit stamping.
   */
  async create(data, currentUser = {}) {
    const user = currentUser?.email ? currentUser : this.currentUser;
    const targetId = data.id || data.vendorId || crypto.randomUUID();

    const payload = {
      ...data,
      id: targetId,
      vendorId: data.vendorId || targetId,
      companyId: data.companyId || user?.companyId || null,
      metadata: {
        ...(data.metadata || {}),
        createdBy: user?.email || "system",
        updatedBy: user?.email || "system",
      },
    };

    return await this.upsertData(targetId, payload, { isCreation: true });
  }

  /**
   * Fetch vendors scoped to the current user's company.
   */
  async fetchVendors({ companyId = "", status = "" } = {}) {
    const user = this.currentUser;
    if (!companyId || companyId.toString().trim() === "") {
      companyId = user?.companyId;
    }

    const filters = [];
    if (companyId) {
      filters.push({ field: "companyId", value: String(companyId) });
    }
    if (status) {
      filters.push({ field: "status", value: status });
    }

    try {
      return (await repo.fetchData(this.collectionName, filters)) || [];
    } catch (error) {
      console.error("[VendorService] Failed to fetch vendors:", error);
      return [];
    }
  }

  /**
   * Fetch a single vendor by ID.
   */
  async fetchById(id) {
    if (!id) {
      console.error("[VendorService] fetchById: ID missing");
      return null;
    }
    try {
      return (await repo.fetchById(this.collectionName, id)) || null;
    } catch (error) {
      console.error(`[VendorService] Failed to fetch vendor ${id}:`, error);
      return null;
    }
  }

  /**
   * Update an existing vendor — preserves historical creation metadata.
   */
  async update(id, data, currentUser = {}) {
    if (!id) throw new Error("Vendor ID is required for update.");
    const user = currentUser?.email ? currentUser : this.currentUser;

    const payload = {
      ...data,
      id,
      metadata: {
        ...(data.metadata || {}),
        updatedBy: user?.email || "system",
      },
    };

    return await this.upsertData(id, payload, { isCreation: false });
  }

  /**
   * Core persistence orchestrator with read-before-write metadata preservation.
   */
  async upsertData(targetId, data, { isCreation = false } = {}) {
    if (!targetId) throw new Error("Target Vendor ID is required");

    let finalData = { ...data };

    if (!isCreation) {
      const existing = await this.fetchById(targetId);
      if (existing) {
        finalData = {
          ...existing,
          ...data,
          metadata: {
            ...(existing.metadata || {}),
            ...(data.metadata || {}),
            createdAt:
              existing.metadata?.createdAt ||
              existing.createdAt ||
              new Date().toISOString(),
            createdBy:
              existing.metadata?.createdBy || existing.createdBy || "system",
          },
        };
      }
    }

    const instance = new VendorModel(
      { ...finalData, id: targetId },
      { isCreation }
    );

    if (!instance.isValid()) {
      throw new Error(
        "Invalid vendor payload: vendorId, vendorName, and a valid GSTIN format (if supplied) are required."
      );
    }

    const payload = instance.toPayload();
    const nowIso = new Date().toISOString();

    payload.metadata = {
      ...(payload.metadata || {}),
      updatedAt: nowIso,
      updatedBy:
        data.metadata?.updatedBy || payload.metadata?.updatedBy || "system",
    };

    return await repo.upsertData(this.collectionName, targetId, payload);
  }

  /**
   * Delete a vendor record.
   */
  async delete(id) {
    if (!id) throw new Error("Vendor ID is required for deletion.");
    try {
      await repo.deleteData(this.collectionName, id);
      return { success: true };
    } catch (error) {
      console.error(`[VendorService] Failed to delete vendor ${id}:`, error);
      throw error;
    }
  }
}

export const vendorService = new VendorService();